import { createFileRoute } from '@tanstack/react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useStore } from '../hooks/useStore';
import { fetchErrors, retryError, resolveError } from '../lib/api';
import { safeArray } from '../lib/safe';

export const Route = createFileRoute('/errors')({
  component: ErrorsPage,
});

function ErrorsPage() {
  const { activeStore } = useStore();
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['errors', activeStore.shop],
    queryFn: () => fetchErrors(activeStore.shop),
  });

  const retryMutation = useMutation({
    mutationFn: (jobId: string) => retryError(activeStore.shop, jobId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['errors', activeStore.shop] }),
  });

  const resolveMutation = useMutation({
    mutationFn: (errorId: string) => resolveError(activeStore.shop, errorId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['errors', activeStore.shop] }),
  });

  const errors = safeArray(data?.errors);
  const unresolved = errors.filter((e) => !e.resolved);
  const resolved = errors.filter((e) => e.resolved);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold text-[var(--ccb-text)]">Errors</h2>
        {unresolved.length > 0 && (
          <span className="text-sm bg-red-50 text-red-700 px-3 py-1 rounded-full font-medium">
            {unresolved.length} unresolved
          </span>
        )}
      </div>

      {isLoading ? (
        <div className="bg-white rounded-xl border border-[var(--ccb-border)] p-12 text-center text-sm text-[var(--ccb-text-secondary)]">
          Loading errors...
        </div>
      ) : errors.length === 0 ? (
        <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-8 text-center">
          <p className="text-emerald-700 font-medium">No errors found</p>
          <p className="text-sm text-emerald-600 mt-1">All products are processing correctly</p>
        </div>
      ) : (
        <>
          {/* Unresolved */}
          {unresolved.length > 0 && (
            <div className="bg-white rounded-xl border border-[var(--ccb-border)] overflow-hidden">
              <div className="px-4 py-3 border-b border-[var(--ccb-border)] bg-red-50/50">
                <h3 className="text-sm font-semibold text-red-800">Unresolved ({unresolved.length})</h3>
              </div>
              <div className="divide-y divide-[var(--ccb-border)]">
                {unresolved.map((err) => (
                  <div key={err.id} className="px-4 py-3 flex items-center justify-between gap-4">
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-[var(--ccb-text)] truncate">{err.title}</p>
                      <p className="text-xs text-[var(--ccb-text-secondary)] mt-0.5 truncate">{err.error}</p>
                      <div className="flex items-center gap-2 mt-0.5">
                        {err.errorType && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded bg-red-50 text-red-600 font-medium">{err.errorType}</span>
                        )}
                        <p className="text-[10px] text-[var(--ccb-text-secondary)]">
                          {new Date(err.timestamp).toLocaleString()}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      {err.jobId && (
                        <button
                          onClick={() => retryMutation.mutate(err.jobId!)}
                          disabled={retryMutation.isPending}
                          className="text-xs font-medium text-[var(--ccb-primary)] hover:underline disabled:opacity-50"
                        >
                          Retry
                        </button>
                      )}
                      <button
                        onClick={() => resolveMutation.mutate(err.id)}
                        disabled={resolveMutation.isPending}
                        className="text-xs font-medium text-[var(--ccb-text-secondary)] hover:underline disabled:opacity-50"
                      >
                        Resolve
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Resolved */}
          {resolved.length > 0 && (
            <div className="bg-white rounded-xl border border-[var(--ccb-border)] overflow-hidden">
              <div className="px-4 py-3 border-b border-[var(--ccb-border)]">
                <h3 className="text-sm font-semibold text-[var(--ccb-text-secondary)]">Resolved ({resolved.length})</h3>
              </div>
              <div className="divide-y divide-[var(--ccb-border)]">
                {resolved.slice(0, 10).map((err) => (
                  <div key={err.id} className="px-4 py-3 opacity-60">
                    <p className="text-sm text-[var(--ccb-text)] truncate">{err.title}</p>
                    <p className="text-xs text-[var(--ccb-text-secondary)] mt-0.5 truncate">{err.error}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
